import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, CheckCircle2, Circle, Search, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

const SUBSISTEMAS_CATALOG = [
  { id: 'neurologico',        nombre: 'Neurológico' },
  { id: 'cardiovascular',     nombre: 'Cardiovascular' },
  { id: 'respiratorio',       nombre: 'Respiratorio' },
  { id: 'digestivo',          nombre: 'Digestivo / Gastrointestinal' },
  { id: 'genitourinario',     nombre: 'Genitourinario' },
  { id: 'musculoesqueletico', nombre: 'Musculoesquelético' },
  { id: 'dermatologico',      nombre: 'Dermatológico' },
  { id: 'orl',                nombre: 'Otorrinolaringológico (ORL)' },
  { id: 'oftalmologico',      nombre: 'Oftalmológico' },
  { id: 'endocrinologico',    nombre: 'Endocrinológico' },
  { id: 'hematologico',       nombre: 'Hematológico / Linfático' },
  { id: 'inmunologico',       nombre: 'Inmunológico / Alergológico' },
  { id: 'psiquiatrico',       nombre: 'Psiquiátrico / Mental' },
  { id: 'reproductivo',       nombre: 'Reproductivo' },
];

interface SubsistemaData {
  id: string;
  nombre: string;
  hallazgos: string;
  normal: boolean;
}

interface Props { id: string; canEdit: boolean; consultaId?: string | null }

export function ExamenFisicoView({ id, canEdit, consultaId }: Props) {
  const qc = useQueryClient();
  const [active, setActive] = useState<SubsistemaData[]>([]);
  const [observaciones, setObservaciones] = useState('');
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const { data: saved, isLoading } = useQuery({
    queryKey: ['examen-fisico', id, consultaId ?? null],
    queryFn: () => api.get(`/api/pacientes/${id}/examen-fisico`, { params: { consultaId: consultaId ?? undefined } }).then(r => r.data),
  });

  useEffect(() => {
    if (saved) {
      const subs: SubsistemaData[] = (saved.subsistemas ?? []).map((s: SubsistemaData) => {
        const catalog = SUBSISTEMAS_CATALOG.find(c => c.id === s.id);
        return { id: s.id, nombre: catalog?.nombre ?? s.nombre, hallazgos: s.hallazgos ?? '', normal: s.normal ?? true };
      });
      setActive(subs);
      setObservaciones(saved.observacionesGenerales ?? '');
      setDirty(false);
    }
  }, [saved]);

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/api/pacientes/${id}/examen-fisico`, {
      subsistemas: active,
      observacionesGenerales: observaciones || null,
      consultaId: consultaId ?? undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['examen-fisico', id, consultaId ?? null] });
      toast.success('Examen físico guardado');
      setDirty(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar')),
  });

  function addSubsistema(sub: typeof SUBSISTEMAS_CATALOG[number]) {
    if (active.some(a => a.id === sub.id)) return;
    setActive(prev => [...prev, { id: sub.id, nombre: sub.nombre, hallazgos: '', normal: true }]);
    setDirty(true);
    setSearch('');
    setShowDropdown(false);
  }

  function removeSubsistema(idx: number) {
    setActive(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  }

  function updateSub(idx: number, field: 'hallazgos' | 'normal', val: string | boolean) {
    setActive(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
    setDirty(true);
  }

  const filtered = SUBSISTEMAS_CATALOG.filter(c =>
    !active.some(a => a.id === c.id) &&
    c.nombre.toLowerCase().includes(search.toLowerCase())
  );
  const activoCount = active.filter(s => s.hallazgos.trim() || !s.normal).length;

  if (isLoading) return <p className="text-center py-8 text-sm text-text-secondary">Cargando...</p>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {active.length === 0
            ? <p className="text-xs text-text-secondary">Sin subsistemas registrados</p>
            : activoCount > 0
              ? <p className="text-xs text-orange-600 font-medium">{activoCount} subsistema{activoCount !== 1 && 's'} con hallazgos</p>
              : <p className="text-xs text-emerald-600 font-medium">{active.length} subsistema{active.length !== 1 && 's'} — sin hallazgos</p>}
        </div>
        {canEdit && dirty && (
          <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
            <Save size={12} /> {saveMutation.isPending ? 'Guardando...' : 'Guardar cambios'}
          </button>
        )}
      </div>

      {/* Search / add */}
      {canEdit && (
        <div className="relative">
          <div className={cn(
            'flex items-center gap-2 px-3 py-2 border rounded-xl bg-white transition-colors',
            showDropdown ? 'border-primary-300 ring-2 ring-primary-100' : 'border-gray-200'
          )}>
            <Search size={13} className="text-text-secondary flex-shrink-0" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="Agregar subsistema... (p.ej. cardiovascular, neurológico)"
              className="flex-1 text-xs outline-none placeholder:text-gray-400"
            />
            {search && (
              <button onClick={() => { setSearch(''); setShowDropdown(false); }}
                className="text-gray-400 hover:text-gray-600"><X size={12} /></button>
            )}
          </div>

          {showDropdown && filtered.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
              {filtered.map(sub => (
                <button key={sub.id} onMouseDown={() => addSubsistema(sub)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-blue-50 transition-colors">
                  <Plus size={11} className="text-blue-500 flex-shrink-0" />
                  <span className="font-medium text-text-primary">{sub.nombre}</span>
                </button>
              ))}
            </div>
          )}

          {showDropdown && search && filtered.length === 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-xs text-text-secondary">
              {SUBSISTEMAS_CATALOG.every(c => active.some(a => a.id === c.id))
                ? 'Todos los subsistemas ya están agregados'
                : 'Sin resultados'}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {active.length === 0 && (
        <div className="text-center py-10 border border-dashed border-gray-200 rounded-2xl">
          <p className="text-sm font-medium text-text-secondary">Sin subsistemas</p>
          <p className="text-xs text-text-secondary mt-1">
            {canEdit ? 'Usa el buscador para agregar subsistemas a explorar' : 'No hay subsistemas registrados'}
          </p>
        </div>
      )}

      {/* Active subsystems */}
      <div className="space-y-1.5">
        {active.map((sub, idx) => {
          const hasHallazgos = sub.hallazgos.trim().length > 0;
          const isAbnormal = !sub.normal;
          return (
            <div key={sub.id} className={cn(
              'rounded-xl border transition-all',
              isAbnormal ? 'border-orange-200 bg-orange-50' : hasHallazgos ? 'border-blue-100 bg-blue-50/50' : 'border-gray-100 bg-white'
            )}>
              <div className="flex items-center gap-3 px-3 py-2.5">
                <button type="button" disabled={!canEdit}
                  onClick={() => updateSub(idx, 'normal', !sub.normal)}
                  className="flex-shrink-0 transition-colors disabled:cursor-default"
                  title={sub.normal ? 'Normal' : 'Anormal'}>
                  {sub.normal
                    ? <CheckCircle2 size={16} className="text-emerald-500" />
                    : <Circle size={16} className="text-orange-500" />}
                </button>
                <p className={cn('text-xs font-semibold flex-1', isAbnormal ? 'text-orange-800' : 'text-text-primary')}>
                  {sub.nombre}
                </p>
                <span className={cn(
                  'text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0',
                  sub.normal ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                )}>
                  {sub.normal ? 'Normal' : 'Anormal'}
                </span>
                {canEdit && (
                  <button onClick={() => removeSubsistema(idx)}
                    className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0">
                    <X size={14} />
                  </button>
                )}
              </div>
              <div className="px-3 pb-3">
                <textarea
                  readOnly={!canEdit}
                  rows={isAbnormal || hasHallazgos ? 2 : 1}
                  value={sub.hallazgos}
                  onChange={e => updateSub(idx, 'hallazgos', e.target.value)}
                  onFocus={e => { if (e.target.rows === 1) e.target.rows = 2; }}
                  placeholder={canEdit ? 'Hallazgos clínicos...' : 'Sin hallazgos registrados'}
                  className={cn(
                    'w-full text-xs resize-none rounded-lg px-2.5 py-2 outline-none transition-colors border',
                    isAbnormal ? 'bg-orange-50 border-orange-200 placeholder:text-orange-400' : 'bg-gray-50 border-gray-200 placeholder:text-gray-400',
                    canEdit ? 'focus:ring-2 focus:ring-primary-300 focus:border-primary-300' : 'cursor-default',
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Observaciones generales */}
      {(active.length > 0 || observaciones) && (
        <div className="bg-white border border-gray-100 rounded-xl p-3">
          <label className="block text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-2">
            Observaciones generales
          </label>
          <textarea
            readOnly={!canEdit}
            rows={3}
            value={observaciones}
            onChange={e => { setObservaciones(e.target.value); setDirty(true); }}
            placeholder={canEdit ? 'Impresión clínica general, comentarios adicionales...' : '—'}
            className="w-full text-xs resize-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-primary-300 placeholder:text-gray-400"
          />
        </div>
      )}

      {canEdit && dirty && (
        <div className="flex justify-end pt-1">
          <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
            <Save size={14} /> {saveMutation.isPending ? 'Guardando...' : 'Guardar examen físico'}
          </button>
        </div>
      )}
    </div>
  );
}
